/* STEP 7e-1 · M3 (owner "Step 7e Methodology Card V1", 30-Oct-2026) — the header badge "Methodology Vn" of the open engagement
   (moved here from Layout.jsx, same look and test id) with its menu: "Open the Methodology page" for everyone and
   "Change methodology version…" for the manager-gate roles (GET /review-roles → methodology.approve = review_roles.MANAGER_GATE).
   The dialog lists the firm's approved and superseded versions only, asks for the reason (required — the screen refuses an
   empty reason with the same EN / AR text as the server) and posts POST /engagements/{id}/methodology-link
   {version_id, reason}; the server writes the Audit Trail row old → new. */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useReviewRoles } from "@/lib/reviewRoles";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CHOOSABLE = ["approved", "superseded"];

export function ChangeVersionDialog({ engId, link, open, onClose, onChanged }) {
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [vid, setVid] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setVid(""); setReason(""); setErr("");
    api.get("/methodology", { _silent: true })
      .then((r) => setRows(asArray(r.data).filter((v) => CHOOSABLE.includes(v.status))))
      .catch(() => setRows([]));
  }, [open]);
  const cur = link?.version;
  const save = async () => {
    if (!reason.trim()) { setErr(t("s7e1.change.reasonRequired")); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/engagements/${engId}/methodology-link`, { version_id: vid, reason }, { _silent: true });
      toast.success(t("s7e1.change.done").replace("{old}", r.data?.old_version ?? "—").replace("{new}", r.data?.version ?? "—"));
      onChanged && onChanged();
      onClose();
    } catch (e) {
      setErr((lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e, "Failed"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="meth-change-dialog">
        <DialogHeader><DialogTitle className="text-base">{t("s7e1.change.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          {cur && (
            <div className="text-xs text-muted-foreground" data-testid="meth-change-current">
              {(link?.linked ? t("s7e1.change.uses") : t("s7e1.change.usesFallback")).replace("{n}", cur.version)}
            </div>
          )}
          <div>
            <label className="text-xs font-medium" htmlFor="meth-change-version">{t("s7e1.change.pick")}</label>
            {rows.length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="meth-change-none">{t("s7e1.change.none")}</div>
            ) : (
              <select id="meth-change-version" className="mt-1 h-9 w-full rounded border bg-white px-2 text-sm dark:bg-slate-900"
                      value={vid} onChange={(e) => setVid(e.target.value)} data-testid="meth-change-version">
                <option value="">{t("s7e1.change.pickPlaceholder")}</option>
                {rows.map((v) => (
                  <option key={v.id} value={v.id} disabled={!!link?.linked && cur?.id === v.id} data-testid={`meth-change-opt-${v.version}`}>
                    {`V${v.version} · ${t(`meth.st.${v.status === "approved" ? "current" : "superseded"}`)} · ${v.filename || ""}`}
                    {link?.linked && cur?.id === v.id ? ` ${t("s7e1.change.current")}` : ""}
                  </option>
                ))}
              </select>
            )}
            <div className="mt-1 text-[11px] text-muted-foreground">{t("s7e1.change.only")}</div>
          </div>
          <div>
            <label className="text-xs font-medium" htmlFor="meth-change-reason">{t("s7e1.change.reason")}</label>
            <Textarea id="meth-change-reason" rows={3} value={reason} placeholder={t("s7e1.change.reasonPh")}
                      onChange={(e) => { setReason(e.target.value); setErr(""); }} data-testid="meth-change-reason" />
          </div>
          {err && <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" role="alert" data-testid="meth-change-error">{err}</div>}
          <div className="text-[11px] text-muted-foreground">{t("s7e1.change.audit")}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="meth-change-cancel">{t("s7e1.change.cancel")}</Button>
          <Button onClick={save} disabled={busy || !vid} data-testid="meth-change-save">{t("s7e1.change.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MethodologyVersionBadge({ engId }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { can } = useReviewRoles();
  const canChange = can("methodology.approve", user?.role);
  const [link, setLink] = useState(null);
  const [menu, setMenu] = useState(false);
  const [dlg, setDlg] = useState(false);
  const box = useRef(null);
  const load = () => {
    if (!engId) { setLink(null); return; }
    api.get(`/engagements/${engId}/methodology-link`, { _silent: true })
      .then((r) => setLink(r.data?.version ? r.data : null)).catch(() => setLink(null));
  };
  useEffect(load, [engId]);
  useEffect(() => {
    if (!menu) return undefined;
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [menu]);
  if (!link?.version) return null;
  const label = (t("meth.headerBadge") || "Methodology V{n}").replace("{n}", link.version.version);
  return (
    <div className="relative order-last" ref={box}>
      <button type="button" className="rounded border px-2 py-0.5 text-[11px] text-slate-700 hover:bg-slate-50 dark:text-slate-200"
              aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)} data-testid="header-methodology-version">
        {label}
      </button>
      {menu && (
        <div role="menu" className="absolute start-0 top-full z-50 mt-1 min-w-[220px] rounded border bg-white p-1 text-xs shadow-md dark:bg-slate-900"
             data-testid="header-methodology-menu">
          <Link to="/methodology" role="menuitem" className="block rounded px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setMenu(false)} data-testid="header-methodology-open">{t("s7e1.change.menuOpen")}</Link>
          {canChange && (
            <button type="button" role="menuitem" className="block w-full rounded px-2 py-1.5 text-start hover:bg-slate-100 dark:hover:bg-slate-800"
                    onClick={() => { setMenu(false); setDlg(true); }} data-testid="header-methodology-change">{t("s7e1.change.menuChange")}</button>
          )}
        </div>
      )}
      <ChangeVersionDialog engId={engId} link={link} open={dlg} onClose={() => setDlg(false)} onChanged={load} />
    </div>
  );
}
